// src/components/UserProfile.tsx
// ENHANCED VERSION - Displays ALL 19 game statistics parameters
// UPDATED: Now uses PageHeader component for consistent header format
// UPDATED: Added Change Password and Change Email functionality
// UPDATED: Reduced StatCard height to 50%
// UPDATED: Shows restricted view for users in cancellation grace period
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.tsx';
import PageHeader from './PageHeader.tsx';
import { TAB_DESCRIPTIONS } from '../constants/tabDescriptions.ts';
import { PIECE_VALUES } from '../data/pieceValues.ts';

// Helper to get piece value from pieceValues.ts
const getPieceValueForHistory = (abbreviation: string, boardSize: string): number => {
  const pieceValues = PIECE_VALUES[abbreviation.toUpperCase()];
  if (!pieceValues) return 1;

  // Convert board size like "9x9" to "9×9" for lookup
  const normalizedSize = boardSize.replace('x', '×');
  const entry = pieceValues.find(v => v.boardSize === normalizedSize);
  return entry?.value || pieceValues.find(v => v.boardSize === '8×8')?.value || 1;
};

interface UserStatistics {
  user_id: string;
  username: string;
  display_name: string | null;
  avatar_url: string | null;
  email?: string;
  current_rating: number;
  highest_ranking: number;
  total_games_played: number;
  total_wins: number;
  total_losses: number;
  total_draws: number;
  total_pieces_captured: number;
  total_pieces_lost: number;
  total_moves_made: number;
  average_game_length: number;
  favorite_board_size: number | null;
  favorite_variant: string | null;
  last_game_played: string;
}

// ENHANCED interface with ALL 19 parameters from GameStatistics
interface GameHistory {
  game_id: number;

  // Core 19 parameters
  game_name: string;
  board_size: string; // Backend returns '8x8' format
  game_result: string;
  winning_player_id: string | null;
  losing_player_id: string | null;
  winning_player_username: string | null; // Added for display
  losing_player_username: string | null; // Added for display
  winning_player_color: string | null;
  losing_player_color: string | null;
  winning_player_ranking: number | null;
  losing_player_ranking: number | null;
  winning_player_score: number;
  losing_player_score: number;
  number_of_moves: number;
  white_pieces_captured: string[];
  black_pieces_captured: string[];
  num_white_pieces_captured: number;
  num_black_pieces_captured: number;
  game_end_datetime: string;
  time_limit: number;
  room_number: string;

  // Additional fields for display
  player_color: string;
  result: 'win' | 'loss' | 'draw';
  ranking_before: number;
  ranking_after: number;
  ranking_change: number;
  end_reason?: string;
}

const API_BASE_URL = (process.env.REACT_APP_API_URL || '').replace(/\/api$/, '');

export const UserProfile: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { isPendingCancellation, daysRemaining, clearCancellationStatus } = useAuth();

  const [statistics, setStatistics] = useState<UserStatistics | null>(null);
  const [gameHistory, setGameHistory] = useState<GameHistory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [expandedGameId, setExpandedGameId] = useState<number | null>(null);
  const [selectedForDeletion, setSelectedForDeletion] = useState<Set<number>>(new Set());
  const [isDeleting, setIsDeleting] = useState(false);

  // Account Settings State
  const [showAccountSettings, setShowAccountSettings] = useState(false);
  const [currentUserEmail, setCurrentUserEmail] = useState<string>('');

  // Membership Level State
  const [membershipLevel, setMembershipLevel] = useState<'Basic' | 'Bronze' | 'Silver' | 'Gold'>('Basic');

  useEffect(() => {
    if (userId) {
      fetchUserData();
    }
  }, [userId]);

  const fetchUserData = async () => {
    try {
      setIsLoading(true);
      
      const statsResponse = await fetch(`${API_BASE_URL}/api/game-statistics/user/${userId}`);
      
      if (!statsResponse.ok) {
        throw new Error(`HTTP error! status: ${statsResponse.status}`);
      }
      
      const statsData = await statsResponse.json();
      
      if (statsData.success) {
        setStatistics(statsData.statistics);
      } else {
        setError('User not found');
      }
      
      // Fetch user details to get email
      const userResponse = await fetch(`${API_BASE_URL}/api/users/${userId}`);
      if (userResponse.ok) {
        const userData = await userResponse.json();
        if (userData.success && userData.user?.email) {
          setCurrentUserEmail(userData.user.email);
        }
      }
      
      const historyResponse = await fetch(`${API_BASE_URL}/api/game-statistics/user/${userId}/history?limit=50`);

      if (!historyResponse.ok) {
        console.warn('Failed to fetch game history');
      } else {
        const historyData = await historyResponse.json();

        if (historyData.success) {
          // Parse captured pieces to ensure they're arrays of strings and calculate scores
          const parsedHistory = historyData.history.map((game: GameHistory) => {
            const boardSize = game.board_size || '8x8';

            // Parse white pieces - could be array of objects or strings
            let whitePieces = game.white_pieces_captured;
            if (typeof whitePieces === 'string') {
              try { whitePieces = JSON.parse(whitePieces || '[]'); } catch { whitePieces = []; }
            }
            if (!Array.isArray(whitePieces)) whitePieces = [];
            // Extract type/abbreviation if objects
            const whitePiecesStrings = whitePieces.map((p: string | { type?: string; abbreviation?: string }) =>
              typeof p === 'string' ? p : (p?.abbreviation || p?.type || 'unknown')
            );

            // Parse black pieces - could be array of objects or strings
            let blackPieces = game.black_pieces_captured;
            if (typeof blackPieces === 'string') {
              try { blackPieces = JSON.parse(blackPieces || '[]'); } catch { blackPieces = []; }
            }
            if (!Array.isArray(blackPieces)) blackPieces = [];
            // Extract type/abbreviation if objects
            const blackPiecesStrings = blackPieces.map((p: string | { type?: string; abbreviation?: string }) =>
              typeof p === 'string' ? p : (p?.abbreviation || p?.type || 'unknown')
            );

            // Calculate scores from captured pieces
            // White's score = value of black pieces they captured
            const whiteScore = blackPiecesStrings.reduce((sum, abbr) =>
              sum + getPieceValueForHistory(abbr, boardSize), 0);
            // Black's score = value of white pieces they captured
            const blackScore = whitePiecesStrings.reduce((sum, abbr) =>
              sum + getPieceValueForHistory(abbr, boardSize), 0);

            // Determine winning/losing player scores
            const winningPlayerScore = game.winning_player_color === 'white' ? whiteScore : blackScore;
            const losingPlayerScore = game.losing_player_color === 'white' ? whiteScore : blackScore;

            return {
              ...game,
              white_pieces_captured: whitePiecesStrings,
              black_pieces_captured: blackPiecesStrings,
              winning_player_score: winningPlayerScore,
              losing_player_score: losingPlayerScore
            };
          });
          setGameHistory(parsedHistory);
        }
      }

      // Fetch membership level from privacy preferences
      const privacyResponse = await fetch(`${API_BASE_URL}/api/users/${userId}/privacy-preferences`);
      if (privacyResponse.ok) {
        const privacyData = await privacyResponse.json();
        if (privacyData.success && privacyData.preferences?.membership_level) {
          setMembershipLevel(privacyData.preferences.membership_level);
        }
      }

    } catch (err) {
      setError('Error loading profile data');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <div style={{ fontSize: '18px', color: '#666' }}>Loading profile...</div>
      </div>
    );
  }

  if (error || !statistics) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <div style={{ fontSize: '18px', color: '#d32f2f', marginBottom: '20px' }}>
          {error || 'Profile not found'}
        </div>
      </div>
    );
  }

  // Show restricted view for users in cancellation grace period
  if (isPendingCancellation) {
    return (
      <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
        <PageHeader
          icon="⚠️"
          title="Account Pending Cancellation"
          description="Your account is scheduled for deletion. Reactivate to regain access."
        />

        <div style={{
          marginTop: '30px',
          backgroundColor: 'white',
          padding: '30px',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          border: '3px solid #ff9800'
        }}>
          <div style={{
            textAlign: 'center',
            padding: '20px',
            backgroundColor: '#fff3e0',
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '15px' }}>⏳</div>
            <h2 style={{ color: '#e65100', margin: '0 0 10px 0' }}>
              Account Access Restricted
            </h2>
            <p style={{ fontSize: '16px', color: '#666', margin: '0 0 10px 0' }}>
              Your account will be permanently deleted in <strong style={{ color: '#e65100' }}>{daysRemaining} days</strong>.
            </p>
            <p style={{ fontSize: '14px', color: '#888', margin: 0 }}>
              All features are locked until you reactivate your account.
            </p>
          </div>

          {/* Reactivation Section */}
          <CancelAccountSection
            userId={userId || ''}
            onReactivated={clearCancellationStatus}
          />
        </div>
      </div>
    );
  }

  const winRate = statistics.total_games_played > 0
    ? ((statistics.total_wins / statistics.total_games_played) * 100).toFixed(1)
    : '0.0';

  return (
    <div style={{ padding: '10px', maxWidth: '1400px', margin: '0 auto' }}>
      <PageHeader
        icon={TAB_DESCRIPTIONS.profile.icon}
        title={TAB_DESCRIPTIONS.profile.label}
        description={TAB_DESCRIPTIONS.profile.description}
      />

      <div style={{ marginTop: '20px' }}>
        {/* Membership Rewards, Privacy Preferences & Cookie Categories Section */}
        <PrivacyCookieSettings userId={userId || ''} onMembershipUpdate={setMembershipLevel} />

        {/* Account Settings Section */}
        <div style={{
          backgroundColor: 'white',
          padding: '21px',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          border: '3px solid #1976d2',
          marginBottom: '10px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '17px' }}>
            <div>
              <h2 style={{ margin: 0, color: '#1976d2' }}>⚙️ Account Settings</h2>
              <p style={{ margin: '5px 0 0 0', fontSize: '14px', color: '#888' }}>
                Update your email address and password
              </p>
            </div>
            <button
              onClick={() => setShowAccountSettings(!showAccountSettings)}
              style={{
                padding: '8px 16px',
                backgroundColor: '#1976d2',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              {showAccountSettings ? 'Hide' : 'Show'} Settings
            </button>
          </div>

          {showAccountSettings && (
            <div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
                gap: '10px',
                marginBottom: '17px'
              }}>
                {/* Change Email Section */}
                <ChangeEmailForm
                  userId={userId || ''}
                  currentEmail={currentUserEmail}
                  onEmailChanged={(newEmail) => setCurrentUserEmail(newEmail)}
                />

                {/* Change Password Section */}
                <ChangePasswordForm userId={userId || ''} />
              </div>

              {/* Cancel Account Section */}
              <CancelAccountSection userId={userId || ''} />
            </div>
          )}
        </div>

        {/* Statistics Grid with User Profile Card */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '10px',
          marginBottom: '10px'
        }}>
          {/* User Profile Card */}
          <div style={{
            backgroundColor: 'white',
            padding: '7px 12px',
            borderRadius: '10px',
            boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
            border: '2px solid #7b1fa2',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <div style={{ fontSize: '20px' }}>👤</div>
            <div style={{ fontSize: '14px', fontWeight: '700', color: '#666' }}>
              {statistics.display_name || statistics.username}
            </div>
          </div>

          {/* Membership Level Badge */}
          <div style={{
            backgroundColor: membershipLevel === 'Gold' ? '#fffacd' :
                           membershipLevel === 'Silver' ? '#f5f5f5' :
                           membershipLevel === 'Bronze' ? '#fdf5e6' : '#f9f9f9',
            padding: '7px 12px',
            borderRadius: '10px',
            boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
            border: `2px solid ${
              membershipLevel === 'Gold' ? '#FFD700' :
              membershipLevel === 'Silver' ? '#C0C0C0' :
              membershipLevel === 'Bronze' ? '#CD7F32' : '#808080'
            }`,
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <div style={{ fontSize: '20px' }}>
              {membershipLevel === 'Gold' ? '🥇' :
               membershipLevel === 'Silver' ? '🥈' :
               membershipLevel === 'Bronze' ? '🥉' : '📋'}
            </div>
            <div style={{
              fontSize: '14px',
              fontWeight: '700',
              color: membershipLevel === 'Gold' ? '#B8860B' :
                     membershipLevel === 'Silver' ? '#757575' :
                     membershipLevel === 'Bronze' ? '#CD7F32' : '#808080'
            }}>
              {membershipLevel} Member
            </div>
          </div>

          <CompactStatCard
            title="Current Rating"
            value={statistics.current_rating.toString()}
            icon="📊"
            color="#6a1b9a"
          />
          <CompactStatCard
            title="Peak Rating"
            value={statistics.highest_ranking.toString()}
            icon="⭐"
            color="#f57c00"
          />
        </div>

        {/* Game Stats Row - Compact Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(5, 1fr)',
          gap: '10px',
          marginBottom: '10px'
        }}>
          <CompactStatCard
            title="Total Games"
            value={statistics.total_games_played.toString()}
            icon="🎮"
            color="#1976d2"
          />
          <CompactStatCard
            title="Win Rate"
            value={`${winRate}%`}
            icon="🏆"
            color="#2e7d32"
          />
          <CompactStatCard
            title="Total Wins"
            value={statistics.total_wins.toString()}
            icon="✅"
            color="#2e7d32"
          />
          <CompactStatCard
            title="Total Losses"
            value={statistics.total_losses.toString()}
            icon="❌"
            color="#c62828"
          />
          <CompactStatCard
            title="Total Draws"
            value={statistics.total_draws.toString()}
            icon="🤝"
            color="#666"
          />
        </div>

        {/* COMPLETE Game History with ALL 19 Parameters */}
        <div style={{
          backgroundColor: 'white',
          padding: '25px',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          border: '3px solid #6a1b9a'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
              <h2 style={{ margin: 0, color: '#6a1b9a' }}>Complete Game History ({gameHistory.length})</h2>
              <span style={{ fontSize: '13px', color: '#888', fontStyle: 'italic' }}>Last 24 games are saved</span>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              {showHistory && (
                <button
                  onClick={async () => {
                    if (selectedForDeletion.size === 0) {
                      alert('Please select games to delete by checking the boxes on the right side of each card.');
                      return;
                    }
                    if (window.confirm(`Delete ${selectedForDeletion.size} selected game(s)?`)) {
                      setIsDeleting(true);
                      try {
                        const deletePromises = Array.from(selectedForDeletion).map(gameId =>
                          fetch(`${API_BASE_URL}/api/game-statistics/game/${gameId}`, { method: 'DELETE' })
                        );
                        await Promise.all(deletePromises);
                        setGameHistory(prev => prev.filter(g => !selectedForDeletion.has(g.game_id)));
                        setSelectedForDeletion(new Set());
                      } catch (err) {
                        console.error('Error deleting games:', err);
                      } finally {
                        setIsDeleting(false);
                      }
                    }
                  }}
                  disabled={isDeleting}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: isDeleting ? '#ef9a9a' : '#f44336',
                    color: 'white',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: isDeleting ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}
                >
                  {isDeleting ? 'Deleting...' : `Delete${selectedForDeletion.size > 0 ? ` (${selectedForDeletion.size})` : ''}`}
                </button>
              )}
              <button
                onClick={() => setShowHistory(!showHistory)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#7b1fa2',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                {showHistory ? 'Hide' : 'Show'} History
              </button>
            </div>
          </div>

          {showHistory && gameHistory.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              {gameHistory.map((game, index) => (
                <GameCard
                  key={game.game_id}
                  game={game}
                  itemNumber={index + 1}
                  totalItems={gameHistory.length}
                  isExpanded={expandedGameId === game.game_id}
                  onToggle={() => setExpandedGameId(expandedGameId === game.game_id ? null : game.game_id)}
                  currentUserId={userId || ''}
                  isSelected={selectedForDeletion.has(game.game_id)}
                  onSelectToggle={(selected) => {
                    setSelectedForDeletion(prev => {
                      const next = new Set(prev);
                      if (selected) {
                        next.add(game.game_id);
                      } else {
                        next.delete(game.game_id);
                      }
                      return next;
                    });
                  }}
                />
              ))}
            </div>
          )}

          {showHistory && gameHistory.length === 0 && (
            <div style={{ textAlign: 'center', padding: '30px', color: '#666' }}>
              No game history available yet
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// =====================================
// CHANGE EMAIL FORM COMPONENT
// =====================================
const ChangeEmailForm: React.FC<{
  userId: string;
  currentEmail: string;
  onEmailChanged: (newEmail: string) => void;
}> = ({ userId, currentEmail, onEmailChanged }) => {
  const [newEmail, setNewEmail] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    // Validation
    if (!newEmail || !confirmEmail || !password) {
      setMessage({ type: 'error', text: 'All fields are required' });
      return;
    }

    if (!validateEmail(newEmail)) {
      setMessage({ type: 'error', text: 'Please enter a valid email address' });
      return;
    }

    if (newEmail !== confirmEmail) {
      setMessage({ type: 'error', text: 'Email addresses do not match' });
      return;
    }

    if (newEmail.toLowerCase() === currentEmail.toLowerCase()) {
      setMessage({ type: 'error', text: 'New email must be different from current email' });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/users/${userId}/change-email`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          new_email: newEmail,
          current_password: password
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setMessage({ type: 'success', text: 'Email updated successfully!' });
        onEmailChanged(newEmail);
        setNewEmail('');
        setConfirmEmail('');
        setPassword('');
      } else {
        setMessage({ type: 'error', text: data.message || 'Failed to update email' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
      console.error('Change email error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{
      padding: '20px',
      backgroundColor: '#f5f5f5',
      borderRadius: '10px',
      border: '2px solid #e0e0e0'
    }}>
      <h3 style={{ margin: '0 0 15px 0', color: '#1976d2', fontSize: '16px' }}>
        📧 Change Email Address
      </h3>
      
      <div style={{ 
        marginBottom: '15px', 
        padding: '10px', 
        backgroundColor: '#e3f2fd', 
        borderRadius: '6px',
        fontSize: '14px'
      }}>
        <strong>Current Email:</strong>{' '}
        {currentEmail ? (
          <span style={{ color: '#1976d2' }}>{currentEmail}</span>
        ) : (
          <span style={{ color: '#999', fontStyle: 'italic' }}>Not available - ensure /api/users endpoint is configured</span>
        )}
      </div>

      <form onSubmit={handleSubmit}>
        <FormInput
          label="New Email Address"
          type="email"
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          placeholder="Enter new email address"
          disabled={isSubmitting}
        />
        
        <FormInput
          label="Confirm New Email"
          type="email"
          value={confirmEmail}
          onChange={(e) => setConfirmEmail(e.target.value)}
          placeholder="Confirm new email address"
          disabled={isSubmitting}
        />
        
        <FormInput
          label="Current Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Enter your current password"
          disabled={isSubmitting}
        />

        {message && (
          <div style={{
            padding: '10px',
            marginBottom: '15px',
            borderRadius: '6px',
            backgroundColor: message.type === 'success' ? '#e8f5e9' : '#ffebee',
            color: message.type === 'success' ? '#2e7d32' : '#c62828',
            fontSize: '14px'
          }}>
            {message.type === 'success' ? '✓ ' : '✗ '}{message.text}
          </div>
        )}

        <button
          type="submit"
          disabled={isSubmitting}
          style={{
            width: '100%',
            padding: '12px',
            backgroundColor: isSubmitting ? '#90caf9' : '#1976d2',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: 'bold',
            cursor: isSubmitting ? 'not-allowed' : 'pointer',
            transition: 'background-color 0.2s'
          }}
        >
          {isSubmitting ? 'Updating...' : 'Update Email'}
        </button>
      </form>
    </div>
  );
};

// =====================================
// CHANGE PASSWORD FORM COMPONENT
// =====================================
const ChangePasswordForm: React.FC<{
  userId: string;
}> = ({ userId }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const validatePassword = (password: string): { valid: boolean; message: string } => {
    if (password.length < 8) {
      return { valid: false, message: 'Password must be at least 8 characters long' };
    }
    if (!/[A-Z]/.test(password)) {
      return { valid: false, message: 'Password must contain at least one uppercase letter' };
    }
    if (!/[a-z]/.test(password)) {
      return { valid: false, message: 'Password must contain at least one lowercase letter' };
    }
    if (!/[0-9]/.test(password)) {
      return { valid: false, message: 'Password must contain at least one number' };
    }
    return { valid: true, message: '' };
  };

  const getPasswordStrength = (password: string): { strength: number; label: string; color: string } => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.length >= 12) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;

    if (strength <= 2) return { strength, label: 'Weak', color: '#f44336' };
    if (strength <= 4) return { strength, label: 'Medium', color: '#ff9800' };
    return { strength, label: 'Strong', color: '#4caf50' };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setMessage({ type: 'error', text: 'All fields are required' });
      return;
    }

    const passwordValidation = validatePassword(newPassword);
    if (!passwordValidation.valid) {
      setMessage({ type: 'error', text: passwordValidation.message });
      return;
    }

    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }

    if (currentPassword === newPassword) {
      setMessage({ type: 'error', text: 'New password must be different from current password' });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/users/${userId}/change-password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setMessage({ type: 'success', text: 'Password updated successfully!' });
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setMessage({ type: 'error', text: data.message || 'Failed to update password' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
      console.error('Change password error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const passwordStrength = getPasswordStrength(newPassword);

  return (
    <div style={{
      padding: '20px',
      backgroundColor: '#f5f5f5',
      borderRadius: '10px',
      border: '2px solid #e0e0e0'
    }}>
      <h3 style={{ margin: '0 0 15px 0', color: '#1976d2', fontSize: '16px' }}>
        🔒 Change Password
      </h3>

      <form onSubmit={handleSubmit}>
        <FormInput
          label="Current Password"
          type={showPasswords ? 'text' : 'password'}
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          placeholder="Enter your current password"
          disabled={isSubmitting}
        />
        
        <FormInput
          label="New Password"
          type={showPasswords ? 'text' : 'password'}
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          placeholder="Enter new password"
          disabled={isSubmitting}
        />

        {/* Password Strength Indicator */}
        {newPassword && (
          <div style={{ marginBottom: '15px' }}>
            <div style={{
              height: '6px',
              backgroundColor: '#e0e0e0',
              borderRadius: '3px',
              overflow: 'hidden',
              marginBottom: '5px'
            }}>
              <div style={{
                height: '100%',
                width: `${(passwordStrength.strength / 6) * 100}%`,
                backgroundColor: passwordStrength.color,
                transition: 'width 0.3s, background-color 0.3s'
              }} />
            </div>
            <div style={{ fontSize: '12px', color: passwordStrength.color, fontWeight: 'bold' }}>
              Password Strength: {passwordStrength.label}
            </div>
          </div>
        )}
        
        <FormInput
          label="Confirm New Password"
          type={showPasswords ? 'text' : 'password'}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          placeholder="Confirm new password"
          disabled={isSubmitting}
        />

        {/* Show/Hide Passwords Toggle */}
        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '13px', color: '#666' }}>
            <input
              type="checkbox"
              checked={showPasswords}
              onChange={(e) => setShowPasswords(e.target.checked)}
              style={{ marginRight: '8px' }}
            />
            Show passwords
          </label>
        </div>

        {/* Password Requirements */}
        <div style={{
          marginBottom: '15px',
          padding: '10px',
          backgroundColor: '#fff3e0',
          borderRadius: '6px',
          fontSize: '12px',
          color: '#e65100'
        }}>
          <strong>Password Requirements:</strong>
          <ul style={{ margin: '5px 0 0 0', paddingLeft: '20px' }}>
            <li style={{ color: newPassword.length >= 8 ? '#4caf50' : '#666' }}>
              At least 8 characters
            </li>
            <li style={{ color: /[A-Z]/.test(newPassword) ? '#4caf50' : '#666' }}>
              One uppercase letter
            </li>
            <li style={{ color: /[a-z]/.test(newPassword) ? '#4caf50' : '#666' }}>
              One lowercase letter
            </li>
            <li style={{ color: /[0-9]/.test(newPassword) ? '#4caf50' : '#666' }}>
              One number
            </li>
          </ul>
        </div>

        {message && (
          <div style={{
            padding: '10px',
            marginBottom: '15px',
            borderRadius: '6px',
            backgroundColor: message.type === 'success' ? '#e8f5e9' : '#ffebee',
            color: message.type === 'success' ? '#2e7d32' : '#c62828',
            fontSize: '14px'
          }}>
            {message.type === 'success' ? '✓ ' : '✗ '}{message.text}
          </div>
        )}

        <button
          type="submit"
          disabled={isSubmitting}
          style={{
            width: '100%',
            padding: '12px',
            backgroundColor: isSubmitting ? '#90caf9' : '#1976d2',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: 'bold',
            cursor: isSubmitting ? 'not-allowed' : 'pointer',
            transition: 'background-color 0.2s'
          }}
        >
          {isSubmitting ? 'Updating...' : 'Update Password'}
        </button>
      </form>
    </div>
  );
};

// =====================================
// CANCEL ACCOUNT SECTION COMPONENT
// =====================================
const CancelAccountSection: React.FC<{
  userId: string;
  onReactivated?: () => void;  // Callback after successful reactivation
}> = ({ userId, onReactivated }) => {
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'warning'; text: string } | null>(null);
  const [accountStatus, setAccountStatus] = useState<{
    isPendingCancellation: boolean;
    cancellationDate: string | null;
    daysRemaining: number | null;
  } | null>(null);

  // Check account cancellation status on mount
  useEffect(() => {
    checkCancellationStatus();
  }, [userId]);

  const checkCancellationStatus = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/users/${userId}/cancellation-status`);
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setAccountStatus({
            isPendingCancellation: data.isPendingCancellation,
            cancellationDate: data.cancellationConfirmedAt,
            daysRemaining: data.daysRemaining
          });
        }
      }
    } catch (err) {
      console.error('Error checking cancellation status:', err);
    }
  };

  const handleInitiateCancellation = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    if (!password) {
      setMessage({ type: 'error', text: 'Password is required to cancel your account' });
      return;
    }

    if (confirmText !== 'CANCEL MY ACCOUNT') {
      setMessage({ type: 'error', text: 'Please type "CANCEL MY ACCOUNT" exactly to confirm' });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/users/${userId}/initiate-cancellation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          password: password
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setMessage({ 
          type: 'success', 
          text: 'Cancellation email sent! Please check your email to confirm account cancellation.' 
        });
        setShowCancelDialog(false);
        setPassword('');
        setConfirmText('');
      } else {
        setMessage({ type: 'error', text: data.message || 'Failed to initiate cancellation' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
      console.error('Cancel account error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReactivateAccount = async () => {
    setIsSubmitting(true);
    setMessage(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/users/${userId}/reactivate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setMessage({ type: 'success', text: 'Account reactivated successfully!' });
        setAccountStatus(null);
        checkCancellationStatus();
        // Clear the cancellation status in AuthContext
        if (onReactivated) {
          onReactivated();
        }
      } else {
        setMessage({ type: 'error', text: data.message || 'Failed to reactivate account' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{
      padding: '20px',
      backgroundColor: '#fff5f5',
      borderRadius: '10px',
      border: '2px solid #f44336'
    }}>
      <h3 style={{ margin: '0 0 10px 0', color: '#c62828', fontSize: '16px' }}>
        ⚠️ Cancel Account
      </h3>
      <p style={{ margin: '0 0 15px 0', fontSize: '13px', color: '#666' }}>
        Permanently delete your account and all associated data
      </p>

      {/* Show reactivation option if account is pending cancellation */}
      {accountStatus?.isPendingCancellation && (
        <div style={{
          padding: '15px',
          backgroundColor: '#fff3e0',
          borderRadius: '8px',
          border: '2px solid #ff9800',
          marginBottom: '15px'
        }}>
          <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#e65100', marginBottom: '8px' }}>
            ⏳ Account Scheduled for Deletion
          </div>
          <div style={{ fontSize: '13px', color: '#666', marginBottom: '10px' }}>
            Your account will be permanently deleted in <strong>{accountStatus.daysRemaining} days</strong>.
            <br />
            Cancellation confirmed on: {accountStatus.cancellationDate ? new Date(accountStatus.cancellationDate).toLocaleDateString() : 'N/A'}
          </div>
          <button
            onClick={handleReactivateAccount}
            disabled={isSubmitting}
            style={{
              padding: '10px 20px',
              backgroundColor: isSubmitting ? '#a5d6a7' : '#4caf50',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 'bold',
              cursor: isSubmitting ? 'not-allowed' : 'pointer'
            }}
          >
            {isSubmitting ? 'Reactivating...' : '✓ Reactivate My Account'}
          </button>
        </div>
      )}

      {message && (
        <div style={{
          padding: '10px',
          marginBottom: '15px',
          borderRadius: '6px',
          backgroundColor: message.type === 'success' ? '#e8f5e9' : message.type === 'warning' ? '#fff3e0' : '#ffebee',
          color: message.type === 'success' ? '#2e7d32' : message.type === 'warning' ? '#e65100' : '#c62828',
          fontSize: '14px'
        }}>
          {message.type === 'success' ? '✓ ' : message.type === 'warning' ? '⚠ ' : '✗ '}{message.text}
        </div>
      )}

      {!showCancelDialog ? (
        <button
          onClick={() => setShowCancelDialog(true)}
          disabled={accountStatus?.isPendingCancellation}
          style={{
            padding: '10px 20px',
            backgroundColor: accountStatus?.isPendingCancellation ? '#e0e0e0' : '#f44336',
            color: accountStatus?.isPendingCancellation ? '#999' : 'white',
            border: 'none',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: 'bold',
            cursor: accountStatus?.isPendingCancellation ? 'not-allowed' : 'pointer'
          }}
        >
          {accountStatus?.isPendingCancellation ? 'Cancellation Pending' : 'Cancel My Account'}
        </button>
      ) : (
        <div style={{
          padding: '20px',
          backgroundColor: 'white',
          borderRadius: '8px',
          border: '2px solid #c62828'
        }}>
          <div style={{ 
            fontSize: '14px', 
            fontWeight: 'bold', 
            color: '#c62828', 
            marginBottom: '15px' 
          }}>
            ⚠️ This action cannot be undone after the 7-day grace period
          </div>
          
          <div style={{
            padding: '12px',
            backgroundColor: '#ffebee',
            borderRadius: '6px',
            fontSize: '13px',
            color: '#666',
            marginBottom: '15px'
          }}>
            <strong>What happens when you cancel:</strong>
            <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
              <li>A confirmation email will be sent to your registered email</li>
              <li>You must click the link to confirm cancellation</li>
              <li>Your account will be immediately locked</li>
              <li>You have 7 days to reactivate your account</li>
              <li>After 7 days, all your data will be permanently deleted</li>
            </ul>
          </div>

          <form onSubmit={handleInitiateCancellation}>
            <FormInput
              label="Enter your password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Your current password"
              disabled={isSubmitting}
            />

            <div style={{ marginBottom: '15px' }}>
              <label style={{
                display: 'block',
                marginBottom: '6px',
                fontSize: '13px',
                fontWeight: '600',
                color: '#333'
              }}>
                Type "CANCEL MY ACCOUNT" to confirm
              </label>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="CANCEL MY ACCOUNT"
                disabled={isSubmitting}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  fontSize: '14px',
                  border: '2px solid #f44336',
                  borderRadius: '6px',
                  outline: 'none',
                  boxSizing: 'border-box',
                  backgroundColor: isSubmitting ? '#f5f5f5' : 'white'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                type="button"
                onClick={() => {
                  setShowCancelDialog(false);
                  setPassword('');
                  setConfirmText('');
                  setMessage(null);
                }}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: '#e0e0e0',
                  color: '#333',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                Go Back
              </button>
              <button
                type="submit"
                disabled={isSubmitting || confirmText !== 'CANCEL MY ACCOUNT'}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: isSubmitting || confirmText !== 'CANCEL MY ACCOUNT' ? '#ef9a9a' : '#c62828',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  cursor: isSubmitting || confirmText !== 'CANCEL MY ACCOUNT' ? 'not-allowed' : 'pointer'
                }}
              >
                {isSubmitting ? 'Processing...' : 'Send Cancellation Email'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

// =====================================
// MEMBERSHIP REWARDS, PRIVACY & COOKIES COMPONENT
// =====================================
interface MembershipInfo {
  points: number;
  level: 'Basic' | 'Bronze' | 'Silver' | 'Gold';
  monthlyAiGamesLimit: number;
  monthlyMultiplayerGamesLimit: number;
  levelDescription: string;
}

const PrivacyCookieSettings: React.FC<{
  userId: string;
  onMembershipUpdate?: (level: 'Basic' | 'Bronze' | 'Silver' | 'Gold') => void;
}> = ({ userId, onMembershipUpdate }) => {
  const [showPrivacySettings, setShowPrivacySettings] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Cookie preferences
  const [preferenceCookies, setPreferenceCookies] = useState(true);
  const [analyticsCookies, setAnalyticsCookies] = useState(true);
  const [marketingCookies, setMarketingCookies] = useState(false);

  // Privacy preferences
  const [doNotSell, setDoNotSell] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);

  // Membership info
  const [membership, setMembership] = useState<MembershipInfo>({
    points: 0,
    level: 'Basic',
    monthlyAiGamesLimit: 10,
    monthlyMultiplayerGamesLimit: 10,
    levelDescription: 'Play up to 10 AI games and 10 multiplayer games per month'
  });

  // Game usage tracking
  const [gameUsage, setGameUsage] = useState<{
    aiGamesThisMonth: number;
    multiplayerGamesThisMonth: number;
    aiGamesRemaining: number;
    multiplayerGamesRemaining: number;
    aiGamesLimit: number;
    multiplayerGamesLimit: number;
    daysLeftInMonth: number;
    monthStartDate: string;
  } | null>(null);

  useEffect(() => {
    if (userId) {
      fetchPrivacyPreferences();
      fetchGameLimits();
    }
  }, [userId]);

  // Calculate days left in current month
  const calculateDaysLeftInMonth = () => {
    const now = new Date();
    const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return lastDayOfMonth.getDate() - now.getDate();
  };

  // Fetch game limits and usage
  const fetchGameLimits = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/users/${userId}/game-limits`);
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          const now = new Date();
          const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
          setGameUsage({
            aiGamesThisMonth: data.usage?.aiGamesThisMonth || 0,
            multiplayerGamesThisMonth: data.usage?.multiplayerGamesThisMonth || 0,
            aiGamesRemaining: data.remaining?.aiGamesRemaining || 0,
            multiplayerGamesRemaining: data.remaining?.multiplayerGamesRemaining || 0,
            aiGamesLimit: data.limits?.aiGamesLimit || 10,
            multiplayerGamesLimit: data.limits?.multiplayerGamesLimit || 10,
            daysLeftInMonth: calculateDaysLeftInMonth(),
            monthStartDate: monthStart.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
          });
        }
      }
    } catch (err) {
      console.error('Error fetching game limits:', err);
    }
  };

  // Calculate preview points based on current toggle states
  const calculatePreviewPoints = () => {
    let points = 0;
    if (analyticsCookies) points += 10;
    if (marketingConsent) points += 20;
    if (marketingCookies) points += 35;
    if (!doNotSell) points += 35;
    return points;
  };

  const getPreviewLevel = (points: number) => {
    if (points >= 70) return { level: 'Gold', color: '#FFD700', icon: '🥇' };
    if (points >= 35) return { level: 'Silver', color: '#C0C0C0', icon: '🥈' };
    if (points >= 10) return { level: 'Bronze', color: '#CD7F32', icon: '🥉' };
    return { level: 'Basic', color: '#808080', icon: '📋' };
  };

  const fetchPrivacyPreferences = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/users/${userId}/privacy-preferences`);

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.preferences) {
          setDoNotSell(data.preferences.doNotSell || false);
          setMarketingConsent(data.preferences.marketingConsent || false);
          setPreferenceCookies(data.preferences.preferenceCookies !== false);
          setAnalyticsCookies(data.preferences.analyticsCookies !== false);
          setMarketingCookies(data.preferences.marketingCookies || false);
        }
        if (data.membership) {
          setMembership(data.membership);
        }
      }
    } catch (err) {
      console.error('Error fetching privacy preferences:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSavePreferences = async () => {
    setIsSaving(true);
    setMessage(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/users/${userId}/privacy-preferences`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          doNotSell,
          marketingConsent,
          preferenceCookies,
          analyticsCookies,
          marketingCookies
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setMessage({ type: 'success', text: 'Your privacy and cookie preferences have been saved.' });
        console.log('Save response data:', data);
        console.log('Membership data:', data.membership);
        console.log('onMembershipUpdate exists:', !!onMembershipUpdate);
        if (data.membership) {
          setMembership(data.membership);
          // Update parent component's membership level display
          console.log('Membership level:', data.membership.level);
          if (onMembershipUpdate && data.membership.level) {
            console.log('Calling onMembershipUpdate with:', data.membership.level);
            onMembershipUpdate(data.membership.level);
          }
        }
        // Refresh game limits to reflect new membership tier
        await fetchGameLimits();
      } else {
        setMessage({ type: 'error', text: data.message || 'Failed to save preferences.' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
      console.error('Save preferences error:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const previewPoints = calculatePreviewPoints();
  const previewLevel = getPreviewLevel(previewPoints);

  return (
    <div style={{
      backgroundColor: 'white',
      padding: '21px',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      border: '3px solid #4CAF50',
      marginBottom: '10px'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '17px' }}>
        <div>
          <h2 style={{ margin: 0, color: '#4CAF50' }}>🏆 Membership Rewards, Privacy Preferences & Cookie Categories</h2>
          <p style={{ margin: '5px 0 0 0', fontSize: '14px', color: '#888' }}>
            Earn rewards, manage privacy options, and control cookie preferences
          </p>
        </div>
        <button
          onClick={() => setShowPrivacySettings(!showPrivacySettings)}
          style={{
            padding: '8px 16px',
            backgroundColor: '#4CAF50',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          {showPrivacySettings ? 'Hide' : 'Show'} Settings
        </button>
      </div>

      {showPrivacySettings && (
        <div>
          {isLoading ? (
            <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
              Loading your preferences...
            </div>
          ) : (
            <>
              {/* Save Button and Links - Top */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '15px' }}>
                <button
                  onClick={handleSavePreferences}
                  disabled={isSaving}
                  style={{
                    padding: '10px 24px',
                    backgroundColor: isSaving ? '#a5d6a7' : '#4CAF50',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    cursor: isSaving ? 'not-allowed' : 'pointer'
                  }}
                >
                  {isSaving ? 'Saving...' : 'Save Preferences'}
                </button>
                <span style={{ fontSize: '13px', color: '#666' }}>
                  View our{' '}
                  <a href="/privacy" style={{ color: '#4CAF50', textDecoration: 'none' }}>Privacy Policy</a>
                  {' '}and{' '}
                  <a href="/privacy#cookie-policy" style={{ color: '#4CAF50', textDecoration: 'none' }}>Cookie Policy</a>
                </span>
              </div>

              {message && (
                <div style={{
                  padding: '10px',
                  marginBottom: '15px',
                  borderRadius: '6px',
                  backgroundColor: message.type === 'success' ? '#e8f5e9' : '#ffebee',
                  color: message.type === 'success' ? '#2e7d32' : '#c62828',
                  fontSize: '14px'
                }}>
                  {message.type === 'success' ? '✓ ' : '✗ '}{message.text}
                </div>
              )}

              {/* Compact Membership Rewards Section */}
              <div style={{
                padding: '12px',
                backgroundColor: '#f5f5f5',
                borderRadius: '10px',
                border: `3px solid ${previewLevel.color}`,
                marginBottom: '15px'
              }}>
                <h3 style={{ margin: '0 0 8px 0', color: '#333', fontSize: '16px' }}>
                  🏆 Membership Rewards
                  <span style={{ fontSize: '15px', fontWeight: '600', color: '#666', marginLeft: '10px' }}>
                    — Set your Privacy Preferences and Cookie Categories below to establish your point total and resulting membership rewards.
                  </span>
                </h3>

                {/* Current Level Display */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '10px',
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  border: `2px solid ${previewLevel.color}`,
                  marginBottom: '10px'
                }}>
                  <div style={{ fontSize: '36px', lineHeight: 1 }}>
                    {previewLevel.icon}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontSize: '20px',
                      fontWeight: 'bold',
                      color: previewLevel.color,
                      textShadow: previewLevel.level === 'Gold' ? '0 0 10px rgba(255, 215, 0, 0.5)' : 'none'
                    }}>
                      {previewLevel.level} Level
                      <span style={{ fontSize: '13px', fontWeight: 'normal', color: '#666', marginLeft: '12px' }}>
                        — {previewLevel.level === 'Gold' && 'Play up to 100 AI games and 100 multiplayer games per month'}
                        {previewLevel.level === 'Silver' && 'Play up to 50 AI games and 50 multiplayer games per month'}
                        {previewLevel.level === 'Bronze' && 'Play up to 25 AI games and 25 multiplayer games per month'}
                        {previewLevel.level === 'Basic' && 'Play up to 10 AI games and 10 multiplayer games per month'}
                      </span>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#333' }}>
                      {previewPoints}
                    </div>
                    <div style={{ fontSize: '11px', color: '#666' }}>points</div>
                  </div>
                </div>

                {/* Points Progress Bar */}
                <div style={{ marginBottom: '10px', minWidth: '500px' }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: '10px',
                    color: '#666',
                    marginBottom: '2px'
                  }}>
                    <span>0</span>
                    <span style={{ color: '#CD7F32' }}>10</span>
                    <span style={{ color: '#C0C0C0' }}>35</span>
                    <span style={{ color: '#FFD700' }}>70</span>
                    <span>100</span>
                  </div>
                  <div style={{
                    height: '8px',
                    backgroundColor: '#e0e0e0',
                    borderRadius: '4px',
                    overflow: 'hidden',
                    position: 'relative'
                  }}>
                    <div style={{ position: 'absolute', left: '10%', top: 0, bottom: 0, width: '1px', backgroundColor: '#CD7F32' }} />
                    <div style={{ position: 'absolute', left: '35%', top: 0, bottom: 0, width: '1px', backgroundColor: '#C0C0C0' }} />
                    <div style={{ position: 'absolute', left: '70%', top: 0, bottom: 0, width: '1px', backgroundColor: '#FFD700' }} />
                    <div style={{
                      height: '100%',
                      width: `${previewPoints}%`,
                      background: previewPoints >= 70
                        ? 'linear-gradient(90deg, #CD7F32 0%, #C0C0C0 50%, #FFD700 100%)'
                        : previewPoints >= 35
                        ? 'linear-gradient(90deg, #CD7F32 0%, #C0C0C0 100%)'
                        : '#CD7F32',
                      borderRadius: '4px',
                      transition: 'width 0.3s ease'
                    }} />
                  </div>
                </div>

                {/* Points Breakdown and Tiers - Side by Side */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  {/* Points Breakdown */}
                  <div style={{
                    padding: '8px',
                    backgroundColor: 'white',
                    borderRadius: '6px',
                    border: '1px solid #e0e0e0'
                  }}>
                    <div style={{ fontSize: '15px', fontWeight: 'bold', color: '#333', marginBottom: '6px' }}>
                      Points Breakdown:
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '3px', fontSize: '15px' }}>
                      <span style={{ color: analyticsCookies ? '#4CAF50' : '#999' }}>
                        {analyticsCookies ? '✓' : '○'} Analytics Cookies
                      </span>
                      <span style={{ color: analyticsCookies ? '#4CAF50' : '#999', fontWeight: 'bold' }}>
                        {analyticsCookies ? '+10' : '0'}
                      </span>
                      <span style={{ color: marketingConsent ? '#4CAF50' : '#999' }}>
                        {marketingConsent ? '✓' : '○'} Marketing Comms
                      </span>
                      <span style={{ color: marketingConsent ? '#4CAF50' : '#999', fontWeight: 'bold' }}>
                        {marketingConsent ? '+20' : '0'}
                      </span>
                      <span style={{ color: marketingCookies ? '#4CAF50' : '#999' }}>
                        {marketingCookies ? '✓' : '○'} Marketing Cookies
                      </span>
                      <span style={{ color: marketingCookies ? '#4CAF50' : '#999', fontWeight: 'bold' }}>
                        {marketingCookies ? '+35' : '0'}
                      </span>
                      <span style={{ color: !doNotSell ? '#4CAF50' : '#999' }}>
                        {!doNotSell ? '✓' : '○'} Allow Data Sharing
                      </span>
                      <span style={{ color: !doNotSell ? '#4CAF50' : '#999', fontWeight: 'bold' }}>
                        {!doNotSell ? '+35' : '0'}
                      </span>
                      <div style={{ gridColumn: '1 / -1', borderTop: '1px solid #e0e0e0', margin: '2px 0' }} />
                      <span style={{ fontWeight: 'bold', color: '#333' }}>Total</span>
                      <span style={{ fontWeight: 'bold', color: previewLevel.color }}>{previewPoints} pts</span>
                    </div>
                  </div>

                  {/* Membership Levels - 2x2 Grid */}
                  <div style={{
                    padding: '12px',
                    backgroundColor: 'white',
                    borderRadius: '6px',
                    border: '1px solid #e0e0e0'
                  }}>
                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#333', marginBottom: '9px' }}>
                      Membership Levels:
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                      <div style={{ textAlign: 'center', padding: '6px', backgroundColor: previewLevel.level === 'Basic' ? '#f0f0f0' : 'transparent', borderRadius: '4px' }}>
                        <span style={{ fontSize: '21px' }}>📋</span>
                        <span style={{ fontSize: '17px', fontWeight: 'bold', color: '#808080', marginLeft: '6px' }}>Basic</span>
                        <div style={{ fontSize: '14px', color: '#666' }}>0-9 pts</div>
                        <div style={{ fontSize: '12px', color: '#888' }}>10 AI + 10 MP/mo</div>
                      </div>
                      <div style={{ textAlign: 'center', padding: '6px', backgroundColor: previewLevel.level === 'Bronze' ? '#fdf5e6' : 'transparent', borderRadius: '4px' }}>
                        <span style={{ fontSize: '21px' }}>🥉</span>
                        <span style={{ fontSize: '17px', fontWeight: 'bold', color: '#CD7F32', marginLeft: '6px' }}>Bronze</span>
                        <div style={{ fontSize: '14px', color: '#666' }}>10-34 pts</div>
                        <div style={{ fontSize: '12px', color: '#888' }}>25 AI + 25 MP/mo</div>
                      </div>
                      <div style={{ textAlign: 'center', padding: '6px', backgroundColor: previewLevel.level === 'Silver' ? '#f5f5f5' : 'transparent', borderRadius: '4px' }}>
                        <span style={{ fontSize: '21px' }}>🥈</span>
                        <span style={{ fontSize: '17px', fontWeight: 'bold', color: '#757575', marginLeft: '6px' }}>Silver</span>
                        <div style={{ fontSize: '14px', color: '#666' }}>35-69 pts</div>
                        <div style={{ fontSize: '12px', color: '#888' }}>50 AI + 50 MP/mo</div>
                      </div>
                      <div style={{ textAlign: 'center', padding: '6px', backgroundColor: previewLevel.level === 'Gold' ? '#fffacd' : 'transparent', borderRadius: '4px' }}>
                        <span style={{ fontSize: '21px' }}>🥇</span>
                        <span style={{ fontSize: '17px', fontWeight: 'bold', color: '#B8860B', marginLeft: '6px' }}>Gold</span>
                        <div style={{ fontSize: '14px', color: '#666' }}>70+ pts</div>
                        <div style={{ fontSize: '12px', color: '#888' }}>100 AI + 100 MP/mo</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Game Usage This Month Section */}
              {gameUsage && (
                <div style={{
                  padding: '12px',
                  backgroundColor: '#e3f2fd',
                  borderRadius: '10px',
                  border: '2px solid #2196F3',
                  marginBottom: '12px'
                }}>
                  <h3 style={{ margin: '0 0 10px 0', color: '#1976d2', fontSize: '16px' }}>
                    🎮 Game Usage This Month
                    <span style={{ fontSize: '12px', fontWeight: 'normal', color: '#666', marginLeft: '10px' }}>
                      — Month started {gameUsage.monthStartDate} • {gameUsage.daysLeftInMonth} days remaining
                    </span>
                  </h3>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    {/* AI Games */}
                    <div style={{
                      padding: '12px',
                      backgroundColor: 'white',
                      borderRadius: '8px',
                      border: '1px solid #bbdefb'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        <span style={{ fontSize: '24px' }}>🤖</span>
                        <span style={{ fontSize: '15px', fontWeight: 'bold', color: '#333' }}>AI Games</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                        <div>
                          <span style={{ fontSize: '28px', fontWeight: 'bold', color: '#1976d2' }}>
                            {gameUsage.aiGamesThisMonth}
                          </span>
                          <span style={{ fontSize: '14px', color: '#666' }}> / {gameUsage.aiGamesLimit}</span>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: '14px', color: gameUsage.aiGamesRemaining > 0 ? '#4CAF50' : '#f44336', fontWeight: 'bold' }}>
                            {gameUsage.aiGamesRemaining} remaining
                          </div>
                        </div>
                      </div>
                      {/* Progress bar */}
                      <div style={{
                        height: '6px',
                        backgroundColor: '#e0e0e0',
                        borderRadius: '3px',
                        marginTop: '8px',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          height: '100%',
                          width: `${Math.min(100, (gameUsage.aiGamesThisMonth / gameUsage.aiGamesLimit) * 100)}%`,
                          backgroundColor: gameUsage.aiGamesRemaining > 0 ? '#4CAF50' : '#f44336',
                          borderRadius: '3px',
                          transition: 'width 0.3s ease'
                        }} />
                      </div>
                    </div>

                    {/* Multiplayer Games */}
                    <div style={{
                      padding: '12px',
                      backgroundColor: 'white',
                      borderRadius: '8px',
                      border: '1px solid #bbdefb'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        <span style={{ fontSize: '24px' }}>🌐</span>
                        <span style={{ fontSize: '15px', fontWeight: 'bold', color: '#333' }}>Multiplayer Games</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                        <div>
                          <span style={{ fontSize: '28px', fontWeight: 'bold', color: '#1976d2' }}>
                            {gameUsage.multiplayerGamesThisMonth}
                          </span>
                          <span style={{ fontSize: '14px', color: '#666' }}> / {gameUsage.multiplayerGamesLimit}</span>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: '14px', color: gameUsage.multiplayerGamesRemaining > 0 ? '#4CAF50' : '#f44336', fontWeight: 'bold' }}>
                            {gameUsage.multiplayerGamesRemaining} remaining
                          </div>
                        </div>
                      </div>
                      {/* Progress bar */}
                      <div style={{
                        height: '6px',
                        backgroundColor: '#e0e0e0',
                        borderRadius: '3px',
                        marginTop: '8px',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          height: '100%',
                          width: `${Math.min(100, (gameUsage.multiplayerGamesThisMonth / gameUsage.multiplayerGamesLimit) * 100)}%`,
                          backgroundColor: gameUsage.multiplayerGamesRemaining > 0 ? '#4CAF50' : '#f44336',
                          borderRadius: '3px',
                          transition: 'width 0.3s ease'
                        }} />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Privacy Preferences Section */}
              <div style={{
                padding: '12px',
                backgroundColor: '#f5f5f5',
                borderRadius: '10px',
                border: '2px solid #e0e0e0',
                marginBottom: '12px'
              }}>
                <h3 style={{ margin: '0 0 10px 0', color: '#333', fontSize: '16px' }}>
                  🔒 Privacy Preferences
                </h3>

                {/* Do Not Sell or Share */}
                <div style={{
                  padding: '8px 10px',
                  backgroundColor: doNotSell ? '#fff3e0' : 'white',
                  borderRadius: '6px',
                  border: `2px solid ${doNotSell ? '#ff9800' : '#e0e0e0'}`,
                  marginBottom: '6px'
                }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={doNotSell}
                      onChange={(e) => setDoNotSell(e.target.checked)}
                      disabled={isSaving}
                      style={{ width: '16px', height: '16px', accentColor: '#ff9800', cursor: 'pointer', flexShrink: 0 }}
                    />
                    <span style={{ fontSize: '13px' }}>
                      <strong style={{ color: '#333' }}>Do Not Sell or Share My Personal Information</strong>
                      <span style={{ color: '#666', marginLeft: '8px' }}>— Opt out of data sharing for advertising (CCPA/CPRA, VCDPA, CPA, GDPR)</span>
                    </span>
                  </label>
                </div>

                {/* Marketing Communications */}
                <div style={{
                  padding: '8px 10px',
                  backgroundColor: 'white',
                  borderRadius: '6px',
                  border: '2px solid #e0e0e0'
                }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={marketingConsent}
                      onChange={(e) => setMarketingConsent(e.target.checked)}
                      disabled={isSaving}
                      style={{ width: '16px', height: '16px', accentColor: '#4CAF50', cursor: 'pointer', flexShrink: 0 }}
                    />
                    <span style={{ fontSize: '13px' }}>
                      <strong style={{ color: '#333' }}>Marketing Communications</strong>
                      <span style={{ color: '#666', marginLeft: '8px' }}>— Receive promotional emails about features, tournaments, and offers</span>
                    </span>
                  </label>
                </div>
              </div>

              {/* Cookie Categories Section */}
              <div style={{
                padding: '12px',
                backgroundColor: '#f5f5f5',
                borderRadius: '10px',
                border: '2px solid #e0e0e0'
              }}>
                <h3 style={{ margin: '0 0 10px 0', color: '#333', fontSize: '16px' }}>
                  🍪 Cookie Categories
                </h3>

                {/* Essential Cookies - Always enabled */}
                <div style={{
                  padding: '8px 10px',
                  backgroundColor: 'white',
                  borderRadius: '6px',
                  border: '2px solid #e0e0e0',
                  marginBottom: '6px',
                  opacity: 0.8
                }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <input type="checkbox" checked={true} disabled={true} style={{ width: '16px', height: '16px', accentColor: '#4CAF50', flexShrink: 0 }} />
                    <span style={{ fontSize: '13px' }}>
                      <strong style={{ color: '#333' }}>Essential Cookies (Required)</strong>
                      <span style={{ color: '#666', marginLeft: '8px' }}>— Authentication, security, session management</span>
                    </span>
                  </label>
                </div>

                {/* Preference Cookies */}
                <div style={{
                  padding: '8px 10px',
                  backgroundColor: 'white',
                  borderRadius: '6px',
                  border: '2px solid #e0e0e0',
                  marginBottom: '6px'
                }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={preferenceCookies}
                      onChange={(e) => setPreferenceCookies(e.target.checked)}
                      disabled={isSaving}
                      style={{ width: '16px', height: '16px', accentColor: '#4CAF50', cursor: 'pointer', flexShrink: 0 }}
                    />
                    <span style={{ fontSize: '13px' }}>
                      <strong style={{ color: '#333' }}>Preference Cookies</strong>
                      <span style={{ color: '#666', marginLeft: '8px' }}>— Board appearance, sound settings, game preferences</span>
                    </span>
                  </label>
                </div>

                {/* Analytics Cookies */}
                <div style={{
                  padding: '8px 10px',
                  backgroundColor: 'white',
                  borderRadius: '6px',
                  border: '2px solid #e0e0e0',
                  marginBottom: '6px'
                }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={analyticsCookies}
                      onChange={(e) => setAnalyticsCookies(e.target.checked)}
                      disabled={isSaving}
                      style={{ width: '16px', height: '16px', accentColor: '#4CAF50', cursor: 'pointer', flexShrink: 0 }}
                    />
                    <span style={{ fontSize: '13px' }}>
                      <strong style={{ color: '#333' }}>Analytics Cookies</strong>
                      <span style={{ color: '#666', marginLeft: '8px' }}>— Help us understand usage and improve our website</span>
                    </span>
                  </label>
                </div>

                {/* Marketing/Advertising Cookies */}
                <div style={{
                  padding: '8px 10px',
                  backgroundColor: marketingCookies ? '#fff8e1' : 'white',
                  borderRadius: '6px',
                  border: `2px solid ${marketingCookies ? '#ff9800' : '#e0e0e0'}`
                }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={marketingCookies}
                      onChange={(e) => setMarketingCookies(e.target.checked)}
                      disabled={isSaving}
                      style={{ width: '16px', height: '16px', accentColor: '#ff9800', cursor: 'pointer', flexShrink: 0 }}
                    />
                    <span style={{ fontSize: '13px' }}>
                      <strong style={{ color: '#333' }}>Marketing/Advertising Cookies</strong>
                      <span style={{ color: '#666', marginLeft: '8px' }}>— Display personalized ads based on your interests</span>
                    </span>
                  </label>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// =====================================
// FORM INPUT COMPONENT
// =====================================
const FormInput: React.FC<{
  label: string;
  type: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder: string;
  disabled?: boolean;
}> = ({ label, type, value, onChange, placeholder, disabled }) => (
  <div style={{ marginBottom: '15px' }}>
    <label style={{
      display: 'block',
      marginBottom: '6px',
      fontSize: '13px',
      fontWeight: '600',
      color: '#333'
    }}>
      {label}
    </label>
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      disabled={disabled}
      style={{
        width: '100%',
        padding: '10px 12px',
        fontSize: '14px',
        border: '2px solid #e0e0e0',
        borderRadius: '6px',
        outline: 'none',
        boxSizing: 'border-box',
        transition: 'border-color 0.2s',
        backgroundColor: disabled ? '#f5f5f5' : 'white'
      }}
      onFocus={(e) => e.target.style.borderColor = '#1976d2'}
      onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
    />
  </div>
);

// Helper function to format game result for display
const formatGameResult = (result: string, endReason?: string): string => {
  const r = result?.toLowerCase() || '';
  const e = endReason?.toLowerCase() || '';

  // Check for resignation
  if (r.includes('resign') || e.includes('resign')) {
    if (r.includes('white') || e.includes('white')) return 'Black wins (WR)';
    if (r.includes('black') || e.includes('black')) return 'White wins (BR)';
    return r.includes('white_win') ? 'White wins (R)' : 'Black wins (R)';
  }

  // Check for checkmate
  if (r.includes('checkmate') || e.includes('checkmate')) {
    if (r.includes('white_win') || r.includes('white wins')) return 'White wins (CM)';
    if (r.includes('black_win') || r.includes('black wins')) return 'Black wins (CM)';
    return 'Checkmate';
  }

  // Check for timeout
  if (r.includes('timeout') || e.includes('timeout')) {
    if (r.includes('white_win')) return 'White wins (TO)';
    if (r.includes('black_win')) return 'Black wins (TO)';
    return 'Timeout';
  }

  // Check for draw/stalemate
  if (r.includes('draw') || r.includes('stalemate')) return 'Draw';

  // Handle simple win results
  if (r === 'white_win') return 'White wins';
  if (r === 'black_win') return 'Black wins';

  // Return original if no pattern matched
  return result || 'Unknown';
};

// Game Card Component - Displays ALL 19 Parameters
const GameCard: React.FC<{
  game: GameHistory;
  itemNumber: number;
  totalItems: number;
  isExpanded: boolean;
  onToggle: () => void;
  currentUserId: string;
  isSelected: boolean;
  onSelectToggle: (selected: boolean) => void;
}> = ({ game, itemNumber, totalItems, isExpanded, onToggle, currentUserId, isSelected, onSelectToggle }) => {
  const isWinner = game.result === 'win';
  const isDraw = game.result === 'draw';

  return (
    <div style={{
      backgroundColor: '#f9f9f9',
      border: '2px solid #7b1fa2',
      borderRadius: '6px',
      marginBottom: '8px',
      overflow: 'hidden'
    }}>
      {/* Header - Compact Single Line */}
      <div
        style={{
          padding: '8px 12px',
          backgroundColor: isWinner ? '#e8f5e9' : isDraw ? '#fff3e0' : '#f3e5f5',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          borderBottom: isExpanded ? '2px solid #e0e0e0' : 'none'
        }}
      >
        {/* Clickable content area */}
        <div
          onClick={onToggle}
          style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}
        >
          {/* Item number */}
          <span style={{
            fontSize: '12px',
            fontWeight: 'bold',
            color: '#7b1fa2',
            minWidth: '28px'
          }}>
            #{itemNumber}
          </span>
          <span style={{
            padding: '2px 8px',
            borderRadius: '10px',
            fontSize: '11px',
            fontWeight: 'bold',
            backgroundColor: isWinner ? '#4caf50' : isDraw ? '#ff9800' : '#7b1fa2',
            color: 'white'
          }}>
            {game.result.toUpperCase()}
          </span>
          <span style={{ fontSize: '13px', fontWeight: 'bold', color: '#333' }}>{game.game_name}</span>
          <span style={{ fontSize: '13px', color: '#666' }}>{game.board_size}</span>
          <span style={{ fontSize: '13px', color: '#666' }}>
            {new Date(game.game_end_datetime).toLocaleString()}
          </span>
          <span style={{ fontSize: '13px', color: '#666' }}>Room: {game.room_number}</span>
          <span style={{ fontSize: '13px', color: '#666' }}>Moves: {game.number_of_moves}</span>
        </div>

        {/* Rating change */}
        <div style={{ textAlign: 'right', minWidth: '80px' }}>
          <span style={{
            fontSize: '13px',
            fontWeight: 'bold',
            color: game.ranking_change > 0 ? '#4caf50' : game.ranking_change < 0 ? '#f44336' : '#666'
          }}>
            {game.ranking_change > 0 ? '+' : ''}{game.ranking_change}
          </span>
          <span style={{ fontSize: '11px', color: '#666', marginLeft: '6px' }}>
            {game.ranking_before} → {game.ranking_after}
          </span>
        </div>

        {/* Delete Checkbox - Far Right */}
        <label
          style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}
          onClick={(e) => e.stopPropagation()}
        >
          <span style={{ fontSize: '12px', color: '#888' }}>Delete</span>
          <input
            type="checkbox"
            checked={isSelected}
            onChange={(e) => {
              e.stopPropagation();
              onSelectToggle(e.target.checked);
            }}
            onClick={(e) => e.stopPropagation()}
            style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#7b1fa2' }}
          />
        </label>
      </div>

      {/* Expanded Details - ALL 19 Parameters */}
      {isExpanded && (
        <div style={{ padding: '20px', backgroundColor: 'white' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '15px'
          }}>
            {/* Section 1: Game Info */}
            <div style={{
              padding: '15px',
              backgroundColor: '#f5f5f5',
              borderRadius: '8px',
              border: '1px solid #e0e0e0'
            }}>
              <h4 style={{ margin: '0 0 12px 0', color: '#6a1b9a', fontSize: '14px' }}>
                📋 Game Information
              </h4>
              <ParamRow label="Game Name" value={game.game_name} />
              <ParamRow label="Board Size" value={game.board_size} />
              <ParamRow label="Game Result" value={formatGameResult(game.game_result, game.end_reason)} />
              <ParamRow label="Number of Moves" value={game.number_of_moves.toString()} />
              <ParamRow label="Time Limit" value={game.time_limit > 0 ? `${game.time_limit} minutes` : 'Unlimited'} />
              <ParamRow label="Room Number" value={game.room_number} />
              <ParamRow label="Date/Time" value={new Date(game.game_end_datetime).toLocaleString()} />
            </div>

            {/* Section 2: Player Info */}
            <div style={{
              padding: '15px',
              backgroundColor: '#f5f5f5',
              borderRadius: '8px',
              border: '1px solid #e0e0e0'
            }}>
              <h4 style={{ margin: '0 0 12px 0', color: '#6a1b9a', fontSize: '14px' }}>
                👥 Player Information
              </h4>
              <ParamRow
                label="Winner"
                value={game.winning_player_username || (game.winning_player_id ? 'Unknown' : 'Draw')}
                highlight={game.winning_player_id === currentUserId}
              />
              <ParamRow
                label="Loser"
                value={game.losing_player_username || (game.losing_player_id ? 'Unknown' : 'Draw')}
                highlight={game.losing_player_id === currentUserId}
              />
              <ParamRow label="Winning Color" value={game.winning_player_color || 'N/A'} />
              <ParamRow label="Losing Color" value={game.losing_player_color || 'N/A'} />
              <ParamRow label="Your Color" value={game.player_color} />
            </div>

            {/* Section 3: Rankings */}
            <div style={{ 
              padding: '15px', 
              backgroundColor: '#f5f5f5', 
              borderRadius: '8px',
              border: '1px solid #e0e0e0'
            }}>
              <h4 style={{ margin: '0 0 12px 0', color: '#6a1b9a', fontSize: '14px' }}>
                📈 Rankings & Scores
              </h4>
              <ParamRow 
                label="Winning Player Ranking" 
                value={game.winning_player_ranking?.toString() || 'N/A'} 
              />
              <ParamRow 
                label="Losing Player Ranking" 
                value={game.losing_player_ranking?.toString() || 'N/A'} 
              />
              <ParamRow 
                label="Winning Player Score" 
                value={game.winning_player_score.toString()} 
              />
              <ParamRow 
                label="Losing Player Score" 
                value={game.losing_player_score.toString()} 
              />
              <div style={{ 
                marginTop: '10px', 
                paddingTop: '10px', 
                borderTop: '2px solid #7b1fa2' 
              }}>
                <ParamRow 
                  label="Your Rating Change" 
                  value={`${game.ranking_change > 0 ? '+' : ''}${game.ranking_change}`}
                  highlight
                />
                <ParamRow 
                  label="Your Rating" 
                  value={`${game.ranking_before} → ${game.ranking_after}`}
                  highlight
                />
              </div>
            </div>

            {/* Section 4: Captured Pieces */}
            <div style={{ 
              padding: '15px', 
              backgroundColor: '#f5f5f5', 
              borderRadius: '8px',
              border: '1px solid #e0e0e0',
              gridColumn: 'span 1'
            }}>
              <h4 style={{ margin: '0 0 12px 0', color: '#6a1b9a', fontSize: '14px' }}>
                ♟️ Captured Pieces
              </h4>
              <ParamRow 
                label="White Pieces Captured" 
                value={game.num_white_pieces_captured.toString()} 
              />
              <ParamRow 
                label="Black Pieces Captured" 
                value={game.num_black_pieces_captured.toString()} 
              />
              
              {game.white_pieces_captured.length > 0 && (
                <div style={{ marginTop: '10px' }}>
                  <div style={{ fontSize: '11px', color: '#666', marginBottom: '4px' }}>
                    White Pieces:
                  </div>
                  <div style={{ 
                    fontSize: '16px', 
                    color: '#333',
                    backgroundColor: 'white',
                    padding: '6px',
                    borderRadius: '4px',
                    maxHeight: '60px',
                    overflowY: 'auto'
                  }}>
                    {game.white_pieces_captured.join(', ')}
                  </div>
                </div>
              )}
              
              {game.black_pieces_captured.length > 0 && (
                <div style={{ marginTop: '10px' }}>
                  <div style={{ fontSize: '11px', color: '#666', marginBottom: '4px' }}>
                    Black Pieces:
                  </div>
                  <div style={{ 
                    fontSize: '16px', 
                    color: '#333',
                    backgroundColor: 'white',
                    padding: '6px',
                    borderRadius: '4px',
                    maxHeight: '60px',
                    overflowY: 'auto'
                  }}>
                    {game.black_pieces_captured.join(', ')}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Summary Banner */}
          <div style={{
            marginTop: '15px',
            padding: '12px',
            backgroundColor: isWinner ? '#e8f5e9' : isDraw ? '#fff3e0' : '#ffebee',
            borderRadius: '6px',
            textAlign: 'center',
            fontSize: '13px',
            fontWeight: 'bold',
            color: '#333'
          }}>
            {isWinner ? '🎉 You won this game!' : isDraw ? '🤝 Game ended in a draw' : '😔 You lost this game'}
          </div>
        </div>
      )}
    </div>
  );
};

// Helper component for parameter rows
const ParamRow: React.FC<{
  label: string;
  value: string;
  highlight?: boolean;
}> = ({ label, value, highlight }) => (
  <div style={{ 
    marginBottom: '8px',
    paddingBottom: '6px',
    borderBottom: '1px solid #e0e0e0'
  }}>
    <div style={{ fontSize: '16px', color: '#666', marginBottom: '2px' }}>{label}</div>
    <div style={{ 
      fontSize: '16px', 
      fontWeight: highlight ? 'bold' : 'normal',
      color: highlight ? '#7b1fa2' : '#333'
    }}>
      {value}
    </div>
  </div>
);

// CompactStatCard - horizontal layout with larger text
const CompactStatCard: React.FC<{
  title: string;
  value: string;
  icon: string;
  color: string;
}> = ({ title, value, icon, color }) => (
  <div style={{
    backgroundColor: 'white',
    padding: '7px 12px',
    borderRadius: '10px',
    boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
    border: `2px solid ${color}`,
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  }}>
    <div style={{ fontSize: '20px' }}>{icon}</div>
    <div style={{ fontSize: '14px', fontWeight: '700', color: '#666' }}>{title}</div>
    <div style={{ fontSize: '18px', fontWeight: 'bold', color }}>{value}</div>
  </div>
);

export default UserProfile;
